import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Col, Row } from 'antd';
import { TbUserCircle, TbWorldPin, TbWorldStar } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { marketHoursQueryOptions } from '@/queries';
import {
    AvatarSkeleton,
    ButtonSkeleton,
    CompactActiveAvatarGroup,
    FINANCIAL_MARKET_HUBS_BY_TIMEZONE,
    MarketStatusPopover,
} from '@/shared';
import {
    useActiveWorkspaceId,
    useActiveWorkspaceMembers,
    useMemberStore,
    useUserStore,
    useWorkspaceStore,
} from '@/stores';
import { buildRecommendedTimezones, formatTimezoneLabel, getBrowserTimezone } from '@/utilities';

import {
    AeSVGIcon,
    ChSVGIcon,
    CnSVGIcon,
    DeSVGIcon,
    FrSVGIcon,
    GbSVGIcon,
    HkSVGIcon,
    IeSVGIcon,
    JpSVGIcon,
    KrSVGIcon,
    SgSVGIcon,
    UsSVGIcon,
} from './svgs';

// interface RightProps {}

const timeZoneToSVGIconMap = {
    'America/New_York': UsSVGIcon,
    'Europe/Dublin': IeSVGIcon,
    'Europe/London': GbSVGIcon,
    'Europe/Paris': FrSVGIcon,
    'Europe/Zurich': ChSVGIcon,
    'Europe/Berlin': DeSVGIcon,
    'Asia/Dubai': AeSVGIcon,
    'Asia/Hong_Kong': HkSVGIcon,
    'Asia/Singapore': SgSVGIcon,
    'Asia/Shanghai': CnSVGIcon,
    'Asia/Seoul': KrSVGIcon,
    'Asia/Tokyo': JpSVGIcon,
} as Record<string, string>;

const COMMAND_PALETTE_MARKET_POPOVER_WIDTH = 16 * 24;

const Right: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { selectedUser } = useUserStore();
    const { selectedMember, fetchMembers, loading: loadingMembers } = useMemberStore();
    const { selectedWorkspace } = useWorkspaceStore();
    const activeWorkspaceMembers = useActiveWorkspaceMembers();
    const activeWorkspaceId = useActiveWorkspaceId();
    const requestedMembersWorkspaceIdRef = useRef<string | undefined>();
    const {
        data: exchangeMarketHours = [],
        isError: marketHoursError,
        isPending: marketHoursPending,
    } = useQuery(marketHoursQueryOptions.all());

    const [time, setTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => {
            setTime(new Date());
        }, 1000); // Update every second

        return () => clearInterval(timer); // Cleanup on component unmount
    }, []);

    const workspaceMembersLoaded = activeWorkspaceMembers.length !== 0;

    useEffect(() => {
        if (
            !activeWorkspaceId ||
            requestedMembersWorkspaceIdRef.current === activeWorkspaceId ||
            workspaceMembersLoaded
        ) {
            return;
        }

        requestedMembersWorkspaceIdRef.current = activeWorkspaceId;
        const win = window as typeof window & {
            requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
            cancelIdleCallback?: (handle: number) => void;
        };
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        let idleCallbackId: number | undefined;

        if (win.requestIdleCallback) {
            idleCallbackId = win.requestIdleCallback(() => fetchMembers(), { timeout: 750 });
        } else {
            timeoutId = setTimeout(() => fetchMembers(), 150);
        }

        return () => {
            if (idleCallbackId && win.cancelIdleCallback) {
                win.cancelIdleCallback(idleCallbackId);
            }

            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [activeWorkspaceId, fetchMembers, workspaceMembersLoaded]);

    const browserTimezone = useMemo(() => getBrowserTimezone(), []);
    const preferredTimezone =
        selectedUser?.timeZone ?? selectedUser?.activeMember?.user?.timeZone ?? selectedMember?.user?.timeZone;

    const cities = useMemo(
        () =>
            buildRecommendedTimezones({
                browserTimezone,
                preferredTimezone,
            }),
        [browserTimezone, preferredTimezone],
    );
    const workspaceTimezone = selectedWorkspace ? (selectedWorkspace.workspaceTimeZone ?? 'UTC') : undefined;
    const currentTimezones = useMemo(
        () =>
            [
                preferredTimezone
                    ? {
                          city: formatTimezoneLabel(preferredTimezone),
                          countryCode: 'us',
                          label: 'Preferred',
                          timeZone: preferredTimezone,
                      }
                    : undefined,
                browserTimezone && browserTimezone !== preferredTimezone
                    ? {
                          city: formatTimezoneLabel(browserTimezone),
                          countryCode: 'us',
                          label: 'Local',
                          timeZone: browserTimezone,
                      }
                    : undefined,
                workspaceTimezone
                    ? {
                          city: formatTimezoneLabel(workspaceTimezone),
                          countryCode: 'us',
                          label: 'Workspace',
                          timeZone: workspaceTimezone,
                      }
                    : undefined,
            ].filter(Boolean) as typeof cities,
        [browserTimezone, preferredTimezone, workspaceTimezone],
    );
    const financialHubTimezones = useMemo(
        () => cities.filter(({ label }) => label !== 'Preferred' && label !== 'Local'),
        [cities],
    );
    const visibleTimezoneKeys = useMemo(
        () =>
            new Set([
                ...currentTimezones.map(({ timeZone }) => timeZone),
                ...financialHubTimezones.map(({ timeZone }) => timeZone),
            ]),
        [currentTimezones, financialHubTimezones],
    );
    const membersByTimezone = useMemo(
        () =>
            activeWorkspaceMembers.reduce(
                (acc, member) => {
                    const memberTimezone = member?.user?.timeZone;

                    if (!memberTimezone) {
                        return acc;
                    }

                    acc[memberTimezone] = [...(acc[memberTimezone] ?? []), member];

                    return acc;
                },
                {} as Record<string, typeof activeWorkspaceMembers>,
            ),
        [activeWorkspaceMembers],
    );
    const memberTimezones = useMemo(
        () =>
            Object.entries(membersByTimezone).map(([timeZone]) => ({
                city: formatTimezoneLabel(timeZone),
                countryCode: 'us',
                label: 'Member',
                timeZone,
            })) as typeof cities,
        [membersByTimezone],
    );
    const additionalMemberTimezones = useMemo(
        () => memberTimezones.filter(({ timeZone }) => !visibleTimezoneKeys.has(timeZone)),
        [memberTimezones, visibleTimezoneKeys],
    );
    const showMemberTimezoneSkeleton = loadingMembers && !workspaceMembersLoaded;
    const newDate = new Date();
    const today = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(newDate);
    const sectionLabelStyle = {
        color: token.colorTextTertiary,
        display: 'block',
        fontWeight: 500,
        marginBottom: '0.375rem',
    } as const;
    const formatTwelveHourTime = (timeZone?: string, includeSeconds = false) => {
        try {
            return new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                ...(includeSeconds ? { second: '2-digit' } : {}),
                hour12: true,
                timeZone,
            }).format(time);
        } catch {
            return '';
        }
    };
    const renderTimezoneIcon = (c: (typeof cities)[number]) => {
        if (c?.label === 'Preferred') {
            return (
                <Avatar
                    shape='square'
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '1rem',
                        width: '1rem',
                        marginRight: '0.375rem',
                        verticalAlign: '-0.125rem',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                    }}
                    icon={<TbWorldStar size='0.675rem' style={{ color: token.colorTextTertiary }} />}
                />
            );
        }

        if (c?.label === 'Local' || c?.label === 'Workspace') {
            return (
                <Avatar
                    shape='square'
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '1rem',
                        width: '1rem',
                        marginRight: '0.375rem',
                        verticalAlign: '-0.125rem',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                    }}
                    icon={<TbWorldPin size='0.675rem' style={{ color: token.colorTextTertiary }} />}
                />
            );
        }

        if (c?.label === 'Member') {
            return (
                <Avatar
                    shape='square'
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '1rem',
                        width: '1rem',
                        marginRight: '0.375rem',
                        verticalAlign: '-0.125rem',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                    }}
                    icon={<TbUserCircle size='0.675rem' style={{ color: token.colorTextTertiary }} />}
                />
            );
        }

        if (!timeZoneToSVGIconMap[c?.timeZone]) {
            return null;
        }

        return (
            <Avatar
                shape='square'
                icon={<img alt={c?.timeZone} src={timeZoneToSVGIconMap[c?.timeZone]} />}
                style={{
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    marginRight: '0.375rem',
                    height: '1rem',
                    width: c?.city === 'Geneva' ? '1rem' : '1.5rem',
                }}
            />
        );
    };

    const renderMemberTimezoneAvatars = (timezoneMembers: typeof activeWorkspaceMembers) => {
        if (!timezoneMembers.length) {
            return null;
        }

        return (
            <span
                style={{
                    alignItems: 'center',
                    cursor: 'default',
                    display: 'inline-flex',
                    verticalAlign: 'middle',
                }}
            >
                <CompactActiveAvatarGroup collaborators={timezoneMembers} maxCount={3} size={16} />
            </span>
        );
    };

    const renderMemberTimezoneSkeleton = () => (
        <div
            style={{
                padding: '0.5rem 1rem',
                color: token.colorText,
                borderBottom: `1px solid ${token.colorBorder}`,
            }}
        >
            {[0, 1].map((item) => (
                <Row key={item} style={{ marginBottom: item === 0 ? '0.375rem' : 0 }} align='middle' wrap={false}>
                    <Col flex='auto'>
                        <AvatarSkeleton
                            shape='square'
                            size='small'
                            style={{ height: '1rem', width: '1rem', marginRight: '0.375rem' }}
                        />
                        <ButtonSkeleton
                            size='small'
                            style={{ height: '0.75rem', width: item === 0 ? '5.75rem' : '7rem' }}
                        />
                    </Col>
                    <Col flex='none'>
                        <ButtonSkeleton size='small' style={{ height: '1rem', width: '5rem' }} />
                    </Col>
                </Row>
            ))}
        </div>
    );

    const renderTimezoneRow = (c: (typeof cities)[number], i: number) => {
        const hasContextLabel = c?.label === 'Preferred' || c?.label === 'Local' || c?.label === 'Workspace';
        const isFinancialMarket = c?.label === c?.city && Boolean(FINANCIAL_MARKET_HUBS_BY_TIMEZONE[c.timeZone]);
        const timezoneMembers = membersByTimezone[c.timeZone] ?? [];

        return (
            <Row
                key={`${c?.label}-${c?.timeZone}-${i}`}
                style={{ marginBottom: '0.25rem' }}
                align='middle'
                wrap={false}
            >
                <Col flex='auto' style={{ alignItems: 'center', display: 'inline-flex', minWidth: 0 }}>
                    {isFinancialMarket ? (
                        <MarketStatusPopover
                            city={c.city}
                            flagSrc={timeZoneToSVGIconMap[c.timeZone]}
                            isError={marketHoursError}
                            isLoading={marketHoursPending}
                            marketHours={exchangeMarketHours}
                            now={time}
                            popoverWidth={COMMAND_PALETTE_MARKET_POPOVER_WIDTH}
                            timeZone={c.timeZone}
                        />
                    ) : (
                        <>
                            {renderTimezoneIcon(c)}
                            <small>{hasContextLabel ? `${c?.label} · ${c?.city}` : c?.city}</small>
                        </>
                    )}
                </Col>
                <Col
                    flex='none'
                    style={{
                        alignItems: 'center',
                        display: 'inline-flex',
                        gap: '0.5rem',
                    }}
                >
                    {renderMemberTimezoneAvatars(timezoneMembers)}
                    <small style={{ color: token.colorTextTertiary }}>{formatTwelveHourTime(c.timeZone)}</small>
                </Col>
            </Row>
        );
    };

    return (
        <>
            <div
                style={{
                    borderBottom: `1px solid ${token.colorBorder}`,
                    padding: '0.5rem 1rem',
                    color: token.colorText,
                }}
            >
                <div
                    style={{
                        display: 'inline-block',
                        padding: '0.125rem 0.5rem',
                        paddingBottom: '0.375rem',
                        textAlign: 'center',
                        marginRight: '0.5rem',
                        border: `1px solid ${token.colorBorder}`,
                        backgroundColor: token.colorBorder,
                        borderRadius: token.borderRadius,
                    }}
                >
                    <div style={{ lineHeight: 1 }}>
                        <small style={{ fontSize: 8, fontWeight: 'bold', color: token.red6 }}>
                            {today && today.toUpperCase()}
                        </small>
                    </div>
                    <div style={{ lineHeight: 0.875 }}>
                        <small style={{ fontSize: 12, fontWeight: 'bold', color: token.colorTextSecondary }}>
                            {newDate?.getDate()}
                        </small>
                    </div>
                </div>
                <span style={{ verticalAlign: '0.25rem', color: token.colorTextSecondary }}>
                    {formatTwelveHourTime(undefined, true)}
                </span>
            </div>
            {currentTimezones.length !== 0 && (
                <div
                    style={{
                        padding: '0.5rem 1rem',
                        color: token.colorText,
                        borderBottom: `1px solid ${token.colorBorder}`,
                    }}
                >
                    <small style={sectionLabelStyle}>Current Timezones</small>
                    {currentTimezones.map(renderTimezoneRow)}
                </div>
            )}
            {(showMemberTimezoneSkeleton || additionalMemberTimezones.length !== 0) && (
                <>
                    <div
                        style={{
                            padding: '0.5rem 1rem',
                            color: token.colorText,
                            borderBottom: showMemberTimezoneSkeleton ? undefined : `1px solid ${token.colorBorder}`,
                        }}
                    >
                        <small style={sectionLabelStyle}>Workspace Members</small>
                        {additionalMemberTimezones.map(renderTimezoneRow)}
                    </div>
                    {showMemberTimezoneSkeleton && renderMemberTimezoneSkeleton()}
                </>
            )}
            <div style={{ padding: '0.5rem 1rem', color: token.colorText }}>
                <small style={sectionLabelStyle}>Global Financial Markets</small>
                {financialHubTimezones.map(renderTimezoneRow)}
            </div>
            {/* <Weather /> */}
        </>
    );
};

export default Right;
